import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Flex,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  Image,
  Textarea,
  FormControl,
  FormLabel,
  Checkbox,
  VStack,
  HStack,
  Select,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PAGE_SIZE = 20;
const COLUMN_MAP = {
  id: "ID",
  name_musician: "뮤지션명",
  name_korean: "한글명",
  name_english: "영문명",
  user: "사용자",
  created_date: "생성일",
};

const DEBUT_COUNTRIES = [
  { value: "대한민국", label: "대한민국" },
  { value: "미국", label: "미국" },
  { value: "기타", label: "기타" },
];

const LANGUAGES = [
  { value: "한국어", label: "한국어" },
  { value: "영어", label: "영어" },
  { value: "기타", label: "기타" },
];

const GENDERS = [
  { value: "남성", label: "남성" },
  { value: "여성", label: "여성" },
  { value: "혼성", label: "혼성" },
  { value: "기타", label: "기타" },
];

const isValidStreamingUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const Musicians = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();
  const [inputPage, setInputPage] = useState(page.toString());

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    let query = supabase
      .from("musician")
      .select("*", { count: "exact" })
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (search) {
      query = query.or(
        `name_musician.ilike.%${search}%,user.ilike.%${search}%,id.ilike.%${search}%`
      );
    }

    const { data: rows, count, error } = await query;
    if (!error) {
      setData(rows || []);
      setTotalPages(Math.max(1, Math.ceil((count || 1) / PAGE_SIZE)));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [search, page]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleRowClick = (row) => {
    setSelected(row);
    setEditFields(row);
    setModalOpen(true);
  };

  const handleEditChange = (key, value) => {
    setEditFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selected) return;
    const trimmedStreamingUrl = (editFields.streaming_url || "").trim();
    if (
      trimmedStreamingUrl &&
      !isValidStreamingUrl(trimmedStreamingUrl)
    ) {
      toast({
        title: "스트리밍 URL 형식 오류",
        description:
          "http:// 또는 https://로 시작하는 주소만 입력할 수 있습니다.",
        status: "error",
      });
      return;
    }
    const { error } = await supabase
      .from("musician")
      .update({ ...editFields, streaming_url: trimmedStreamingUrl || null })
      .eq("id", selected.id);
    if (!error) {
      toast({ title: "수정 완료", status: "success" });
      setModalOpen(false);
      fetchData();
    } else {
      toast({
        title: "수정 실패",
        status: "error",
        description: error.message,
      });
    }
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `musician/${
        selected.user
      }/${type}-${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("image")
        .upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: urlData, error: urlError } = await supabase.storage
        .from("image")
        .createSignedUrl(fileName, 60 * 60 * 24 * 7);
      if (urlError || !urlData?.signedUrl) throw urlError;
      setEditFields((prev) => ({ ...prev, [type]: urlData.signedUrl }));
      toast({ title: "이미지 업로드 완료", status: "success" });
    } catch (error) {
      toast({ title: "이미지 업로드 실패", status: "error" });
    }
  };

  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };

  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  return (
    <Box maxW="1400px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center">
        <Input
          placeholder="뮤지션명, 사용자, ID 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          width="300px"
        />
        <Button colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>ID</Th>
              <Th>뮤지션명</Th>
              <Th>한글명</Th>
              <Th>영문명</Th>
              <Th>사용자</Th>
              <Th>생성일</Th>
              <Th>관리</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  <Text color="gray.400">데이터 없음</Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.id}</Td>
                  <Td>
                    <Flex align="center" gap={3}>
                      {row.image_profile ? (
                        <Image
                          src={row.image_profile}
                          alt="profile"
                          boxSize="48px"
                          borderRadius="md"
                        />
                      ) : (
                        <Box boxSize="48px" bg="gray.100" borderRadius="md" />
                      )}
                      <Text>{row.name_musician}</Text>
                    </Flex>
                  </Td>
                  <Td>{row.name_korean}</Td>
                  <Td>{row.name_english}</Td>
                  <Td>{row.user}</Td>
                  <Td>
                    {row.created_date
                      ? new Date(row.created_date).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => handleRowClick(row)}
                    >
                      수정
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>
      {/* 수정 모달 */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="xl">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent maxW="800px" borderRadius="xl" boxShadow="xl">
          <ModalHeader
            bg="blue.50"
            borderTopRadius="xl"
            py={4}
            fontSize="xl"
            fontWeight="bold"
            color="blue.700"
          >
            뮤지션 정보 수정
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            <VStack spacing={6}>
              <HStack width="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    뮤지션 활동명
                  </FormLabel>
                  <Input
                    value={editFields.name_musician ?? ""}
                    onChange={(e) =>
                      handleEditChange("name_musician", e.target.value)
                    }
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    아이돌 여부
                  </FormLabel>
                  <Checkbox
                    isChecked={editFields.idol === "TRUE"}
                    onChange={(e) =>
                      handleEditChange(
                        "idol",
                        e.target.checked ? "TRUE" : "FALSE"
                      )
                    }
                    colorScheme="blue"
                    size="lg"
                  >
                    아이돌 그룹
                  </Checkbox>
                </FormControl>
              </HStack>

              <HStack width="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    뮤지션 본명 (국문)
                  </FormLabel>
                  <Input
                    value={editFields.name_korean ?? ""}
                    onChange={(e) =>
                      handleEditChange("name_korean", e.target.value)
                    }
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    뮤지션 본명 (영문)
                  </FormLabel>
                  <Input
                    value={editFields.name_english ?? ""}
                    onChange={(e) =>
                      handleEditChange("name_english", e.target.value)
                    }
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </HStack>

              <HStack width="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    데뷔 국가
                  </FormLabel>
                  <Select
                    value={editFields.debut_nation ?? ""}
                    onChange={(e) =>
                      handleEditChange("debut_nation", e.target.value)
                    }
                    placeholder="데뷔 국가를 선택하세요"
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  >
                    {DEBUT_COUNTRIES.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    가창 언어
                  </FormLabel>
                  <Select
                    value={editFields.language ?? ""}
                    onChange={(e) =>
                      handleEditChange("language", e.target.value)
                    }
                    placeholder="가창 언어를 선택하세요"
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  >
                    {LANGUAGES.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <HStack width="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    성별
                  </FormLabel>
                  <Select
                    value={editFields.sex_musician ?? ""}
                    onChange={(e) =>
                      handleEditChange("sex_musician", e.target.value)
                    }
                    placeholder="성별을 선택하세요"
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  >
                    {GENDERS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <HStack width="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    데뷔년도
                  </FormLabel>
                  <Input
                    type="number"
                    value={editFields.debut_year ?? ""}
                    onChange={(e) =>
                      handleEditChange("debut_year", e.target.value)
                    }
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    생년월일
                  </FormLabel>
                  <Input
                    type="date"
                    value={editFields.date_of_birth ?? ""}
                    onChange={(e) =>
                      handleEditChange("date_of_birth", e.target.value)
                    }
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontWeight="medium" color="gray.700">
                  자기 소개
                </FormLabel>
                <Textarea
                  value={editFields.introduction ?? ""}
                  onChange={(e) =>
                    handleEditChange("introduction", e.target.value)
                  }
                  rows={3}
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="medium" color="gray.700">
                  활동 이력
                </FormLabel>
                <Textarea
                  value={editFields.biography ?? ""}
                  onChange={(e) =>
                    handleEditChange("biography", e.target.value)
                  }
                  rows={6}
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="medium" color="gray.700">
                  아티스트 스트리밍 URL
                </FormLabel>
                <Input
                  value={editFields.streaming_url ?? ""}
                  onChange={(e) =>
                    handleEditChange("streaming_url", e.target.value)
                  }
                  placeholder="https://..."
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  발매 이력이 없는 신규 아티스트는 공백으로 제출하세요.
                </Text>
              </FormControl>

              <HStack width="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    프로필 이미지
                  </FormLabel>
                  <Flex align="center" gap={2}>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(e.target.files[0], "image_profile")
                      }
                      borderRadius="md"
                      p={1}
                    />
                    {editFields.image_profile && (
                      <Image
                        src={editFields.image_profile}
                        alt="profile"
                        boxSize="48px"
                        borderRadius="md"
                      />
                    )}
                  </Flex>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontWeight="medium" color="gray.700">
                    실물 이미지
                  </FormLabel>
                  <Flex align="center" gap={2}>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(
                          e.target.files[0],
                          "image_verification"
                        )
                      }
                      borderRadius="md"
                      p={1}
                    />
                    {editFields.image_verification && (
                      <Image
                        src={editFields.image_verification}
                        alt="verification"
                        boxSize="48px"
                        borderRadius="md"
                      />
                    )}
                  </Flex>
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>

          <ModalFooter p={4} borderTopWidth={1} borderColor="gray.200">
            <Button variant="ghost" mr={3} onClick={() => setModalOpen(false)}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSave}>
              저장
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Musicians;
